import type { CSSProperties } from 'react'
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'

import styles from './RampingNumberText.module.css'
import { rem } from '@mantine/core'

export const BEHAVIORS = {
  standard: [
    { className: styles.standard0, scale: 1 },
    { className: styles.standard1, scale: 1 },
  ],
  meter: [
    { className: styles.meter0, scale: 1 },
    { className: styles.meter1, scale: 1.5 },
  ],
  scoreBonus: [
    { className: styles.scoreBonus0, scale: 1 },
    { className: styles.scoreBonus1, scale: 1.12 },
    { className: styles.scoreBonus2, scale: 1.32 },
    { className: styles.scoreBonus3, scale: 1.5 },
  ],
} satisfies {
  [name: string]: Array<{
    className: string
    scale: number
  }>
}

export type RampingNumberBehavior = keyof typeof BEHAVIORS

const DEFAULT_MIN_SCALED_DURATION_MS = 100

/**
 * Same scaling as {@link RampingNumberText} when `durationScaleReferenceValue` is set: duration ×
 * (|to − from| / reference), clamped to `[minScaledDuration, duration]`. Use for choreography that
 * must track the on-screen ramp.
 */
export function resolveRampingNumberDurationMs(
  from: number,
  to: number,
  duration: number,
  durationScaleReferenceValue?: number,
  minScaledDuration: number = DEFAULT_MIN_SCALED_DURATION_MS,
): number {
  const baseDur = Math.max(0, duration)
  let rampDur = baseDur
  if (
    durationScaleReferenceValue !== undefined &&
    durationScaleReferenceValue > 0 &&
    Number.isFinite(durationScaleReferenceValue)
  ) {
    const delta = Math.abs(to - from)
    rampDur = baseDur * (delta / durationScaleReferenceValue)
    if (baseDur > 0) {
      rampDur = Math.max(minScaledDuration, rampDur)
      rampDur = Math.min(baseDur, rampDur)
    }
  }
  return rampDur
}

export type RampingNumberTextProps = {
  value: number
  behavior: RampingNumberBehavior
  maxValueVisual: number
  duration: number
  className?: string
  /** Sets root `font-size`; overrides `style.fontSize` when both are set. */
  fz?: CSSProperties['fontSize']
  formatValue?: (value: number) => string
  onDisplayValueChange?: (value: number) => void
  /** Called once when a ramp to `value` finishes (or immediately if already at target). */
  onRampSettled?: (displayedValue: number) => void
  style?: CSSProperties
  /**
   * When set (positive, finite), ramp duration becomes
   * `duration * (|value delta| / durationScaleReferenceValue)`, then clamped between
   * {@link minScaledDuration} and {@link duration} (never longer than `duration`).
   * Example: reference 50 and a 25-point jump → half of {@link duration}.
   */
  durationScaleReferenceValue?: number
  /** Floor for scaled duration when {@link durationScaleReferenceValue} is in effect. */
  minScaledDuration?: number
}

type BehaviorStep = (typeof BEHAVIORS)[RampingNumberBehavior][number]

function clamp01(x: number) {
  return Math.min(1, Math.max(0, x))
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}

/**
 * Blend between a mostly-linear curve and pure cubic ease-out — about halfway
 * between the previous “soft” ramp and `1 - (1 - t)³`. Keeps the arc readable
 * without the pure-cubic tail drag. Share via export for lockstep surfaces
 * ({@link FloatingScoreTag}, gold tracker drain).
 */
export function easeOutSoft(t: number) {
  const cubic = 1 - (1 - t) ** 3
  const soft = 0.65 * t + 0.35 * cubic
  return (soft + cubic) / 2
}

/**
 * While the eased ramp is still in flight (`timeProgress` < 1), keep the visible
 * digits from rounding ahead of the terminal value. The raw eased value is still used
 * for {@link RampingNumberTextProps.onDisplayValueChange} (e.g. meters).
 */
function formatIntegerRampingDisplay(
  rendered: number,
  from: number,
  to: number,
  timeProgress: number,
): string {
  const isFinalFrame = timeProgress >= 1
  if (isFinalFrame) {
    return String(Math.round(to))
  }
  if (to > from) {
    return String(Math.floor(rendered + 1e-9))
  }
  if (to < from) {
    return String(Math.ceil(rendered - 1e-9))
  }
  return String(Math.round(rendered))
}

const TENTHS_QUANT = 10

function quantizeTenthsForRampingDisplay(
  rendered: number,
  from: number,
  to: number,
  timeProgress: number,
): number {
  const isFinalFrame = timeProgress >= 1
  if (isFinalFrame || from === to) {
    return to
  }
  if (to > from) {
    return Math.floor(rendered * TENTHS_QUANT + 1e-9) / TENTHS_QUANT
  }
  if (to < from) {
    return Math.ceil(rendered * TENTHS_QUANT - 1e-9) / TENTHS_QUANT
  }
  return rendered
}

function styleProgress(displayedValue: number, maxValueVisual: number) {
  if (!(maxValueVisual > 0)) {
    return 1
  }
  if (displayedValue >= maxValueVisual) {
    return 1
  }
  return clamp01(displayedValue / maxValueVisual)
}

function pickCrossfadeSteps(
  steps: readonly BehaviorStep[],
  u: number,
): { i: number; j: number; mix: number } {
  const last = steps.length - 1
  if (last <= 0) {
    return { i: 0, j: 0, mix: 0 }
  }
  if (u >= 1) {
    return { i: last, j: last, mix: 0 }
  }
  const scaled = u * last
  const i = Math.min(Math.floor(scaled), last - 1)
  const mix = scaled - i
  return { i, j: Math.min(i + 1, last), mix }
}

function mixScale(steps: readonly BehaviorStep[], u: number) {
  const { i, j, mix } = pickCrossfadeSteps(steps, u)
  return lerp(steps[i]!.scale, steps[j]!.scale, mix)
}

const OPACITY_CUTOFF = 0.004

type LastPaintState = {
  aClass: string
  aOpacity: string
  animA: string
  animB: string
  bClass: string
  bOpacity: string
  bVisibility: string
  scale: number
  text: string
}

function prefersReducedMotionQuery() {
  return (
    typeof globalThis.matchMedia === 'function' &&
    globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function updateLayers(args: {
  lastPaint: LastPaintState
  layerA: HTMLSpanElement
  layerB: HTMLSpanElement
  rootTransform: HTMLSpanElement
  scale: number
  steps: readonly BehaviorStep[]
  text: string
  u: number
  reduceMotion: boolean
}) {
  const {
    lastPaint,
    layerA,
    layerB,
    rootTransform,
    scale,
    steps,
    text,
    u,
    reduceMotion,
  } = args

  if (lastPaint.text !== text) {
    layerA.textContent = text
    layerB.textContent = text
    lastPaint.text = text
  }

  const { i, j, mix } = pickCrossfadeSteps(steps, u)
  const classA = steps[i]!.className
  const classB = steps[j]!.className

  const nextAClass = `${styles.rampingLayer} ${classA}`.trim()
  const nextBClass = `${styles.rampingLayer} ${classB}`.trim()
  if (lastPaint.aClass !== nextAClass) {
    layerA.className = nextAClass
    lastPaint.aClass = nextAClass
  }
  if (lastPaint.bClass !== nextBClass) {
    layerB.className = nextBClass
    lastPaint.bClass = nextBClass
  }

  let aOpacityN: number
  let bOpacityN: number
  if (i === j) {
    aOpacityN = 1
    bOpacityN = 0
  } else {
    /* Back layer stays opaque; front fades in so identical fills never “double-dim”. */
    aOpacityN = 1
    bOpacityN = mix
  }

  const aOpacityStr = aOpacityN > OPACITY_CUTOFF ? String(aOpacityN) : '0'
  const bOpacityStr = bOpacityN > OPACITY_CUTOFF ? String(bOpacityN) : '0'

  if (lastPaint.aOpacity !== aOpacityStr) {
    layerA.style.opacity = aOpacityStr
    lastPaint.aOpacity = aOpacityStr
  }
  if (lastPaint.bOpacity !== bOpacityStr) {
    layerB.style.opacity = bOpacityStr
    lastPaint.bOpacity = bOpacityStr
  }

  const aRunning = !reduceMotion && aOpacityN > OPACITY_CUTOFF
  const bRunning = !reduceMotion && bOpacityN > OPACITY_CUTOFF
  const animAStr = aRunning ? 'running' : 'paused'
  const animBStr = bRunning ? 'running' : 'paused'
  if (lastPaint.animA !== animAStr) {
    layerA.style.animationPlayState = animAStr
    lastPaint.animA = animAStr
  }
  if (lastPaint.animB !== animBStr) {
    layerB.style.animationPlayState = animBStr
    lastPaint.animB = animBStr
  }

  const bVis = bOpacityN > OPACITY_CUTOFF ? 'visible' : 'hidden'
  if (lastPaint.bVisibility !== bVis) {
    layerB.style.visibility = bVis
    lastPaint.bVisibility = bVis
  }

  if (!Number.isFinite(lastPaint.scale) || Math.abs(lastPaint.scale - scale) > 1e-5) {
    rootTransform.style.transform = `scale(${scale})`
    lastPaint.scale = scale
  }
}

const RampingNumberTextInner = forwardRef<HTMLSpanElement, RampingNumberTextProps>(
  function RampingNumberTextInner(
    {
      value,
      behavior,
      maxValueVisual,
      duration,
      className,
      fz,
      formatValue,
      onDisplayValueChange,
      onRampSettled,
      style,
      durationScaleReferenceValue,
      minScaledDuration = DEFAULT_MIN_SCALED_DURATION_MS,
    },
    forwardedRef,
  ) {
    const transformRef = useRef<HTMLSpanElement>(null)
    const gridRef = useRef<HTMLSpanElement>(null)
    const layerARef = useRef<HTMLSpanElement>(null)
    const layerBRef = useRef<HTMLSpanElement>(null)

    useImperativeHandle(forwardedRef, () => transformRef.current as HTMLSpanElement, [])

    const stepsRef = useRef(BEHAVIORS[behavior])
    const maxValueVisualRef = useRef(maxValueVisual)
    const durationRef = useRef(duration)
    const durationScaleReferenceValueRef = useRef(durationScaleReferenceValue)
    const minScaledDurationRef = useRef(minScaledDuration)
    const activeRampDurationRef = useRef(duration)
    const formatValueRef = useRef(formatValue)
    const onDisplayValueChangeRef = useRef(onDisplayValueChange)
    const onRampSettledRef = useRef(onRampSettled)

    useLayoutEffect(() => {
      stepsRef.current = BEHAVIORS[behavior]
      maxValueVisualRef.current = maxValueVisual
      durationRef.current = duration
      durationScaleReferenceValueRef.current = durationScaleReferenceValue
      minScaledDurationRef.current = minScaledDuration
      formatValueRef.current = formatValue
      onDisplayValueChangeRef.current = onDisplayValueChange
      onRampSettledRef.current = onRampSettled
    }, [
      behavior,
      maxValueVisual,
      duration,
      durationScaleReferenceValue,
      minScaledDuration,
      formatValue,
      onDisplayValueChange,
      onRampSettled,
    ])

    const displayedRef = useRef(0)
    const fromRef = useRef(0)
    const toRef = useRef(0)
    const timeProgressRef = useRef(1)
    const animStartMsRef = useRef<number | null>(null)
    const rafRef = useRef<number | null>(null)
    const lastPaintRef = useRef<LastPaintState>({
      aClass: '',
      aOpacity: '',
      animA: '',
      animB: '',
      bClass: '',
      bOpacity: '',
      bVisibility: '',
      scale: Number.NaN,
      text: '',
    })

    const outerStyle = useMemo(
      () => ({
        ...(style ?? {}),
        ...(fz !== undefined ? { fontSize: rem(fz) } : {}),
        lineHeight: 'inherit' as const,
      }),
      [style, fz],
    )

    const kickFrame = () => {
      const transformEl = transformRef.current
      const layerA = layerARef.current
      const layerB = layerBRef.current
      if (!transformEl || !layerA || !layerB) {
        return
      }

      const steps = stepsRef.current
      const rendered = displayedRef.current
      const timeProgress = timeProgressRef.current
      const from = fromRef.current
      const to = toRef.current
      const u = styleProgress(rendered, maxValueVisualRef.current)
      const scale = mixScale(steps, u)
      const text = formatValueRef.current
        ? formatValueRef.current(
            quantizeTenthsForRampingDisplay(rendered, from, to, timeProgress),
          )
        : formatIntegerRampingDisplay(rendered, from, to, timeProgress)
      const reduceMotion = prefersReducedMotionQuery()

      updateLayers({
        lastPaint: lastPaintRef.current,
        layerA,
        layerB,
        reduceMotion,
        rootTransform: transformEl,
        scale,
        steps,
        text,
        u,
      })
      onDisplayValueChangeRef.current?.(rendered)
    }

    useLayoutEffect(() => {
      lastPaintRef.current.aClass = ''
      lastPaintRef.current.bClass = ''
      lastPaintRef.current.animA = ''
      lastPaintRef.current.animB = ''
      lastPaintRef.current.scale = Number.NaN
      timeProgressRef.current = 1
      kickFrame()
    }, [behavior, className, fz, style])

    useEffect(() => {
      const nextTarget = Math.max(0, value)
      if (nextTarget === toRef.current && nextTarget === displayedRef.current) {
        onRampSettledRef.current?.(nextTarget)
        return
      }

      fromRef.current = displayedRef.current
      toRef.current = nextTarget
      animStartMsRef.current =
        typeof performance !== 'undefined' ? performance.now() : Date.now()

      activeRampDurationRef.current = resolveRampingNumberDurationMs(
        displayedRef.current,
        nextTarget,
        durationRef.current,
        durationScaleReferenceValueRef.current,
        minScaledDurationRef.current,
      )

      const tick = (now: number) => {
        const startMs = animStartMsRef.current
        const from = fromRef.current
        const to = toRef.current
        const dur = Math.max(0, activeRampDurationRef.current)

        if (startMs === null) {
          rafRef.current = null
          return
        }

        const t = dur <= 0 ? 1 : clamp01((now - startMs) / dur)
        timeProgressRef.current = t
        const eased = easeOutSoft(t)
        displayedRef.current = from + (to - from) * eased
        kickFrame()

        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick)
        } else {
          displayedRef.current = to
          timeProgressRef.current = 1
          kickFrame()
          rafRef.current = null
          animStartMsRef.current = null
          onRampSettledRef.current?.(to)
        }
      }

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
      }
      rafRef.current = requestAnimationFrame(tick)

      return () => {
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
      }
    }, [value])

    return (
      <span
        ref={transformRef}
        className={[styles.rampingTransformRoot, className].filter(Boolean).join(' ')}
        style={outerStyle}
      >
        <span
          ref={gridRef}
          className={styles.rampingRoot}
          style={{ lineHeight: 'inherit' }}
        >
          <span ref={layerARef} />
          <span ref={layerBRef} />
        </span>
      </span>
    )
  },
)

RampingNumberTextInner.displayName = 'RampingNumberText'

function areRampingNumberTextPropsEqual(
  prev: RampingNumberTextProps,
  next: RampingNumberTextProps,
) {
  return (
    prev.value === next.value &&
    prev.behavior === next.behavior &&
    prev.maxValueVisual === next.maxValueVisual &&
    prev.duration === next.duration &&
    prev.durationScaleReferenceValue === next.durationScaleReferenceValue &&
    prev.minScaledDuration === next.minScaledDuration &&
    prev.className === next.className &&
    prev.fz === next.fz &&
    prev.formatValue === next.formatValue &&
    prev.onDisplayValueChange === next.onDisplayValueChange &&
    prev.onRampSettled === next.onRampSettled &&
    prev.style === next.style
  )
}

export const RampingNumberText = memo(
  RampingNumberTextInner,
  areRampingNumberTextPropsEqual,
)

/**
 * Returns stable args for {@link RampingNumberText} when callers build props from literals.
 * `BEHAVIORS` step arrays are module-stable; this mainly helps object-spread wrappers stay referentially quiet.
 */
export function useRampingNumberTextProps(args: RampingNumberTextProps) {
  const {
    value,
    behavior,
    maxValueVisual,
    duration,
    className,
    fz,
    formatValue,
    onDisplayValueChange,
    onRampSettled,
    style,
    durationScaleReferenceValue,
    minScaledDuration,
  } = args
  return useMemo(
    () => ({
      value,
      behavior,
      maxValueVisual,
      duration,
      className,
      fz,
      formatValue,
      onDisplayValueChange,
      onRampSettled,
      style,
      durationScaleReferenceValue,
      minScaledDuration,
    }),
    [
      value,
      behavior,
      maxValueVisual,
      duration,
      className,
      fz,
      formatValue,
      onDisplayValueChange,
      onRampSettled,
      style,
      durationScaleReferenceValue,
      minScaledDuration,
    ],
  )
}
